import { Link } from "react-router-dom";
import { ThemeToggle } from "../components/ThemeToggle";
import { ArrowLeft } from "lucide-react";

function PageHeader() {
  return (
    <header className="border-b border-border">
      <div className="max-w-[920px] mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
            Chromaforge
          </Link>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted hidden md:inline">
            · Privacy Policy
          </span>
        </div>
        <ThemeToggle />
      </div>
    </header>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-sm font-bold uppercase tracking-[0.2em]">{title}</h2>
    <div className="text-sm leading-relaxed text-foreground/85 space-y-3">{children}</div>
  </section>
);

const Ext = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="underline underline-offset-4 hover:text-foreground"
  >
    {children}
  </a>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader />
      <main className="max-w-[920px] mx-auto px-6 md:px-8 py-10 md:py-14 space-y-10">
        <div className="space-y-3">
          <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted">
            Last updated · 24 February 2026
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted max-w-prose leading-relaxed">
            This Privacy Policy describes how Chromaforge (operating at
            <span className="font-mono"> freecmyk.com</span>) collects, uses and shares
            information when you visit the site.
          </p>
        </div>

        <Section title="01 · Information We Collect">
          <p>
            Chromaforge is a fully client-side colour tool. We do <strong>not</strong> run
            user accounts, do <strong>not</strong> store palettes on a server, and do
            <strong> not</strong> collect personally identifiable information directly.
          </p>
          <p>
            The only data the site itself persists is a single browser
            <span className="font-mono"> localStorage</span> entry
            (<span className="font-mono">cf-theme</span>) which remembers your light/dark
            mode preference. It never leaves your device.
          </p>
        </Section>

        <Section title="02 · Cookies & Similar Technologies">
          <p>
            Third parties — including Google — use cookies, web beacons and similar
            technologies on this site to serve ads and gather usage analytics. These
            technologies may be used to:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Serve ads based on prior visits to this and other websites.</li>
            <li>Measure ad performance and prevent fraud.</li>
            <li>Provide aggregated, anonymised analytics about site usage.</li>
          </ul>
        </Section>

        <Section title="03 · Google AdSense">
          <p>
            Chromaforge uses <strong>Google AdSense</strong> to display advertisements.
            Google's use of advertising cookies enables it and its partners to serve ads
            to you based on your visit to this site and/or other sites on the Internet.
          </p>
          <p>
            You may opt out of personalised advertising by visiting{" "}
            <Ext href="https://www.google.com/settings/ads">Google Ads Settings</Ext>.
            Alternatively, you can opt out of a third-party vendor's use of cookies for
            personalised advertising at{" "}
            <Ext href="https://www.aboutads.info/choices/">aboutads.info/choices</Ext> or{" "}
            <Ext href="https://www.networkadvertising.org/choices/">
              networkadvertising.org/choices
            </Ext>
            .
          </p>
          <p>
            For more details on how Google uses information from sites that use its
            services, see Google's{" "}
            <Ext href="https://policies.google.com/technologies/partner-sites">
              Partner Sites policy
            </Ext>
            .
          </p>
        </Section>

        <Section title="04 · Third-Party Vendors">
          <p>
            In addition to Google AdSense, this site loads resources from the following
            third parties. They may set their own cookies subject to their privacy
            policies:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>
              <strong>Google Fonts</strong> — serves the Work Sans and JetBrains Mono
              webfonts (<Ext href="https://policies.google.com/privacy">privacy</Ext>).
            </li>
            <li>
              <strong>Google AdSense</strong> — serves advertisements
              (<Ext href="https://policies.google.com/technologies/ads">ads policy</Ext>).
            </li>
          </ul>
        </Section>

        <Section title="05 · Your Choices & Rights">
          <p>
            You can disable cookies through your browser settings at any time. Doing so
            may degrade your experience on other sites but will not affect the core
            functionality of Chromaforge, which runs entirely in your browser.
          </p>
          <p>
            If you are located in the European Economic Area, the United Kingdom,
            California, or another region with applicable data-protection laws, you may
            have additional rights including the right to access, correct, or delete
            personal information processed about you. Because Chromaforge does not itself
            collect personal data, such requests should be directed to the relevant
            third-party vendor.
          </p>
        </Section>

        <Section title="06 · Children's Privacy">
          <p>
            Chromaforge is a professional design tool and is not directed at children
            under 13. We do not knowingly collect personal information from children. If
            you believe a child has provided personal information through this site,
            please contact us so we can take appropriate action.
          </p>
        </Section>

        <Section title="07 · Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our
            practices, technology, legal requirements or other factors. The "Last updated"
            date at the top of this page indicates when the policy was last revised.
          </p>
        </Section>

        <Section title="08 · Contact">
          <p>
            Questions about this policy can be sent to
            <span className="font-mono"> hello@freecmyk.com</span>.
          </p>
        </Section>

        <footer className="pt-10 mt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
          >
            ← Back to Chromaforge
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
            freecmyk.com
          </span>
        </footer>
      </main>
    </div>
  );
}
